import { useState } from 'react';
import { trendData, workflowSteps, simulatedMaiaResponse } from '../data/mockData';

const useInsightsState = () => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [workflowStep, setWorkflowStep] = useState(0);
  const [opportunityScore, setOpportunityScore] = useState(null);
  const [briefData, setBriefData] = useState(null);
  const [showMaia, setShowMaia] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState(['claude', 'trendhunter']);
  const [maiaQuery, setMaiaQuery] = useState('');
  const [maiaResponse, setMaiaResponse] = useState(null);
  const [maiaHistory, setMaiaHistory] = useState([
    { id: 1, title: "Americans' Top Worries in 2026: Economy, Politics, AI, Healthcare", timestamp: '2 hours ago' },
    { id: 2, title: "Act as a trend strategist and validate, critique,", timestamp: '1 day ago' },
  ]);
  const [savedResearch, setSavedResearch] = useState([]);

  // Research Briefs - comprehensive MAIA research sessions
  const [researchBriefs, setResearchBriefs] = useState([
    {
      id: 301,
      title: 'Mushroom Coffee Market Analysis',
      query: 'Analyze the mushroom coffee market trends, key players, and consumer sentiment',
      agents: ['Claude', 'Trend Hunter GPT', 'McClatchy News'],
      trendContext: 'Mushroom Coffee',
      summary: 'Market growing at 8.2% CAGR. Key players: Four Sigmatic, RYZE, MUD\\WTR. Consumer sentiment positive among health-conscious 25-44 demo.',
      createdAt: '2026-01-22 14:30',
      status: 'completed'
    },
    {
      id: 302,
      title: 'Gut Health Beverage Competitor Landscape',
      query: 'Who are the main competitors in the prebiotic/probiotic soda space and what is their market positioning?',
      agents: ['Grok', 'OpenAI', 'McClatchy L&E'],
      trendContext: 'Gut Health Sodas',
      summary: 'Olipop leads with $200M+ revenue. Poppi close second. Both targeting health-conscious millennials seeking soda alternatives.',
      createdAt: '2026-01-21 09:15',
      status: 'completed'
    },
    {
      id: 303,
      title: 'Sleep Tech Consumer Behavior Study',
      query: 'What are the emerging consumer behaviors around sleep optimization technology?',
      agents: ['Claude', 'OpenAI'],
      trendContext: null,
      summary: 'Wearables driving adoption. 67% of consumers interested in sleep tracking. Opportunity in content around sleep hygiene.',
      createdAt: '2026-01-20 16:45',
      status: 'completed'
    }
  ]);

  // Quick MAIA search state
  const [quickMaiaQuery, setQuickMaiaQuery] = useState('');
  const [showQuickMaia, setShowQuickMaia] = useState(false);

  // Package Builder state
  const [packages, setPackages] = useState([
    {
      id: 1,
      name: 'Functional Drinks Q1 2026',
      macroTrend: 'Functional Food & Drink',
      status: 'in_progress',
      briefs: [],
      categories: ['Health & Wellness', 'Food & Drink'],
      createdAt: '2026-01-15',
      targetDelivery: '2026-02-01'
    }
  ]);
  const [showPackageModal, setShowPackageModal] = useState(false);

  // Decision History - tracks all approved/declined trends
  const [decisionHistory, setDecisionHistory] = useState([
    {
      id: 101,
      trend: 'Adaptogenic Supplements',
      macro: 'Health & Wellness',
      micro: 'Functional Food & Drink',
      decision: 'approved',
      score: 79,
      decidedAt: '2026-01-18 14:32',
      decidedBy: 'Trend Strategist',
      routing: 'package',
      packageName: 'Functional Drinks Q1 2026'
    },
    {
      id: 102,
      trend: 'Raw Milk Revival',
      macro: 'Health & Wellness',
      micro: 'Functional Food & Drink',
      decision: 'declined',
      score: 42,
      decidedAt: '2026-01-17 09:15',
      decidedBy: 'Trend Strategist',
      routing: null,
      declineReason: 'High regulatory risk, limited audience appeal'
    },
    {
      id: 103,
      trend: 'Protein Coffee',
      macro: 'Health & Wellness',
      micro: 'Functional Food & Drink',
      decision: 'approved',
      score: 86,
      decidedAt: '2026-01-16 11:45',
      decidedBy: 'Trend Strategist',
      routing: 'csa',
      packageName: null
    }
  ]);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Audience Brief state
  const [audienceBriefData, setAudienceBriefData] = useState(null);
  const [showAudienceBrief, setShowAudienceBrief] = useState(false);

  // Dashboard domain tab state
  const [activeDomain, setActiveDomain] = useState('content'); // 'content', 'audience', 'businessDev'

  // Brief Generation Tracker - tracks all generated briefs across trends
  const [generatedBriefs, setGeneratedBriefs] = useState([
    {
      id: 201,
      trendId: 103,
      trend: 'Protein Coffee',
      macro: 'Health & Wellness',
      briefTypes: ['content', 'audience'],
      contentBrief: { status: 'sent', destination: 'csa', sentAt: '2026-01-16 11:50' },
      audienceBrief: { status: 'sent', destination: 'audience_queue', sentAt: '2026-01-16 12:05' },
      createdAt: '2026-01-16'
    },
    {
      id: 202,
      trendId: 101,
      trend: 'Adaptogenic Supplements',
      macro: 'Health & Wellness',
      briefTypes: ['content'],
      contentBrief: { status: 'packaged', destination: 'Functional Drinks Q1 2026', sentAt: null },
      audienceBrief: null,
      createdAt: '2026-01-18'
    }
  ]);

  // Collapsible section states
  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    trendIntake: false,
    packages: false,
    briefSummary: false,
    decisionHistory: true  // Collapsed by default - reference material
  });

  // Notification state (replaces alert() calls)
  const [notification, setNotification] = useState(null);

  const toggleSection = (section) => {
    setSectionsCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Generate pre-populated MAIA prompt based on current trend context
  const generateMaiaPrompt = (trend) => {
    return `Act as a trend strategist and validate, critique, and expand on the following trend opportunity:

**Trend Signal:** ${trend.signal}
**Macro Category:** ${trend.macro}
**Micro Category:** ${trend.micro}

**Current Signals:**
- Search Volume: ${trend.externalSignals.searchVolume}
- Social Mentions: ${trend.externalSignals.socialMentions}
- Retail Expansion: ${trend.externalSignals.retailExpansion}
- Investment Activity: ${trend.externalSignals.investmentActivity}

**Initial Analysis:**
${trend.deepDive.summary}

**Questions to Address:**
1. What consumer pain points does this trend solve?
2. What is the likely trajectory over the next 12-18 months?
3. Who are the key brands and players to watch?
4. What content angles would resonate most with health-conscious consumers?
5. Are there risks or counter-signals we should consider?`;
  };

  const openMaiaWithContext = () => {
    setMaiaQuery(generateMaiaPrompt(selectedTrend));
    setShowMaia(true);
    setMaiaResponse(null);
  };

  const toggleAgent = (agentId) => {
    setSelectedAgents(prev =>
      prev.includes(agentId)
        ? prev.filter(id => id !== agentId)
        : [...prev, agentId]
    );
  };

  const runMaiaQuery = () => {
    setMaiaResponse('loading');
    setTimeout(() => {
      setMaiaResponse(simulatedMaiaResponse);
    }, 1500);
  };

  const saveResearchToOpportunity = () => {
    if (maiaResponse && maiaResponse !== 'loading') {
      const newResearch = {
        id: Date.now(),
        timestamp: new Date().toLocaleString(),
        query: maiaQuery.substring(0, 100) + '...',
        agents: maiaResponse.agents,
        insight: maiaResponse.synthesizedInsight,
        sources: maiaResponse.sources,
        workflowStep: workflowSteps[workflowStep]?.name || 'Unknown'
      };
      setSavedResearch(prev => [newResearch, ...prev]);
      setMaiaHistory(prev => [{
        id: Date.now(),
        title: maiaQuery.substring(0, 50) + '...',
        timestamp: 'Just now'
      }, ...prev]);
    }
    setShowMaia(false);
    setMaiaResponse(null);
  };

  const discardResearch = () => {
    setShowMaia(false);
    setMaiaResponse(null);
  };

  const selectTrend = (trend) => {
    setSelectedTrend(trend);
    setWorkflowStep(1);
    setActiveView('workflow');
  };

  const calculateScore = () => {
    const score = {
      overall: 84,
      signal: 'green',
      factors: {
        trendStrength: { score: 88, weight: 0.25 },
        audienceMatch: { score: 82, weight: 0.25 },
        contentGap: { score: 85, weight: 0.2 },
        revenuePotential: { score: 81, weight: 0.3 },
      },
      recommendation: 'APPROVE - Strong opportunity with clear content gaps and above-average revenue potential'
    };
    setOpportunityScore(score);
    setWorkflowStep(4);
  };

  const approveTrend = () => {
    setBriefData({
      title: 'Mushroom Coffee: Complete Coverage Package',
      priority: 'High',
      targetPublish: '2 weeks',
      contentPlan: [
        { type: 'Pillar', title: 'Mushroom Coffee: The Complete Guide', format: 'Long-form article', wordCount: '2,500-3,000' },
        { type: 'Supporting', title: 'Best Mushroom Coffee Brands Ranked', format: 'Listicle/Review', wordCount: '1,800-2,200' },
        { type: 'Supporting', title: 'Mushroom Coffee vs Regular Coffee: Which Is Better?', format: 'Comparison', wordCount: '1,500' },
        { type: 'Quick Win', title: 'How to Make Mushroom Coffee at Home', format: 'How-to', wordCount: '800-1,000' },
      ],
      seoTargets: ['mushroom coffee', 'best mushroom coffee', 'mushroom coffee benefits', 'lions mane coffee'],
      commerceIntegration: 'Amazon affiliate + brand partnership outreach',
      audienceTargeting: 'Wellness-curious, 25-44, coffee drinkers open to alternatives',
    });
    setWorkflowStep(5);
  };

  // Generate Audience Brief
  const generateAudienceBrief = () => {
    setAudienceBriefData({
      title: `${selectedTrend?.signal}: Audience Strategy`,
      priority: opportunityScore?.overall >= 80 ? 'High' : opportunityScore?.overall >= 60 ? 'Medium' : 'Low',
      generatedAt: new Date().toLocaleString(),

      // Audience Targeting
      audienceSegments: [
        {
          name: 'Primary: Health-Conscious Explorers',
          description: 'Active lifestyle, 25-44, interested in wellness optimization',
          size: '2.4M addressable',
          platforms: ['Instagram', 'YouTube', 'Pinterest'],
          affinities: ['Biohacking', 'Clean eating', 'Fitness', 'Meditation']
        },
        {
          name: 'Secondary: Coffee Enthusiasts',
          description: 'Daily coffee drinkers open to premium alternatives, 28-52',
          size: '1.8M addressable',
          platforms: ['Reddit', 'Twitter/X', 'Podcasts'],
          affinities: ['Specialty coffee', 'Home brewing', 'Food culture']
        },
        {
          name: 'Tertiary: Wellness Skeptics',
          description: 'Curious but research-heavy, need proof points, 30-55',
          size: '900K addressable',
          platforms: ['Google Search', 'YouTube', 'News sites'],
          affinities: ['Science-backed health', 'Consumer reports', 'Reviews']
        }
      ],

      // Distribution Channels
      channelStrategy: [
        {
          channel: 'Organic Search',
          priority: 'High',
          tactics: ['Target informational queries', 'Featured snippet optimization', 'FAQ schema markup'],
          keywords: ['mushroom coffee benefits', 'is mushroom coffee good for you', 'mushroom coffee vs regular coffee'],
          estimatedReach: '450K monthly searches'
        },
        {
          channel: 'Paid Search',
          priority: 'Medium',
          tactics: ['Brand conquest campaigns', 'Non-brand informational', 'Remarketing'],
          keywords: ['buy mushroom coffee', 'best mushroom coffee brand', 'mushroom coffee near me'],
          estimatedCPC: '$1.20-2.40',
          recommendedBudget: '$5K-10K/month test'
        },
        {
          channel: 'Social - Organic',
          priority: 'High',
          tactics: ['UGC-style video content', 'Before/after energy testimonials', 'Recipe content'],
          platforms: ['TikTok', 'Instagram Reels', 'YouTube Shorts'],
          postingCadence: '3-5x weekly'
        },
        {
          channel: 'Social - Paid',
          priority: 'Medium',
          tactics: ['Lookalike audiences from wellness converters', 'Interest targeting stack', 'Video view campaigns'],
          platforms: ['Meta', 'TikTok'],
          estimatedCPM: '$8-15',
          recommendedBudget: '$3K-7K/month test'
        },
        {
          channel: 'Email',
          priority: 'High',
          tactics: ['Segment: wellness-engaged subscribers', 'Drip sequence for consideration', 'Newsletter feature'],
          estimatedAudience: '340K qualified subscribers',
          expectedOpenRate: '28-32%'
        }
      ],

      // Engagement Recommendations
      engagementStrategy: {
        onsite: [
          { tactic: 'Exit-intent popup', description: 'Offer mushroom coffee guide download for email capture', priority: 'High' },
          { tactic: 'Inline content recommendations', description: 'Cross-link to related wellness content', priority: 'High' },
          { tactic: 'Interactive quiz', description: '"Find your perfect coffee alternative" quiz', priority: 'Medium' },
          { tactic: 'Social proof widgets', description: 'Reader comments, expert quotes', priority: 'Medium' }
        ],
        email: [
          { tactic: 'Welcome series mention', description: 'Include in new subscriber wellness track', priority: 'High' },
          { tactic: 'Dedicated send', description: 'Feature article to health segment', priority: 'High' },
          { tactic: 'Newsletter carousel', description: 'Include in weekly wellness roundup', priority: 'Medium' }
        ],
        push: [
          { tactic: 'Breaking trend alert', description: 'Push to wellness-subscribed users on publish', priority: 'Medium' },
          { tactic: 'Follow-up content', description: 'Push when supporting articles publish', priority: 'Low' }
        ],
        social: [
          { tactic: 'Community engagement', description: 'Respond to comments, reshare UGC', priority: 'High' },
          { tactic: 'Poll/question stickers', description: 'Drive engagement on Stories', priority: 'Medium' },
          { tactic: 'Influencer seeding', description: 'Share with wellness micro-influencers', priority: 'Medium' }
        ]
      },

      // Timing Recommendations
      timing: {
        optimalPublishWindow: 'Tuesday-Thursday, 7-9am EST',
        peakSearchPeriod: 'January-March (New Year wellness surge)',
        socialBestTimes: 'Weekdays 6-8am, 12-1pm, 7-9pm',
        emailBestDays: 'Tuesday, Wednesday, Thursday'
      },

      // Success Metrics
      kpis: [
        { metric: 'Organic Traffic', target: '50K sessions in first 30 days', tracking: 'GA4' },
        { metric: 'Email Capture', target: '2,500 new subscribers', tracking: 'ESP' },
        { metric: 'Social Engagement', target: '15K engagements across platforms', tracking: 'Native analytics' },
        { metric: 'Time on Page', target: '4+ minutes average', tracking: 'GA4' },
        { metric: 'Return Visitors', target: '25% return rate', tracking: 'GA4' }
      ]
    });
    setShowAudienceBrief(true);
  };

  // Calculate stats for the summary bar
  const getStats = () => {
    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

    const recentDecisions = decisionHistory.filter(d => {
      const decisionDate = new Date(d.decidedAt);
      return decisionDate >= oneWeekAgo;
    });

    return {
      pendingReview: trendData.length,
      packagesInProgress: packages.filter(p => p.status === 'in_progress').length,
      approvedThisWeek: recentDecisions.filter(d => d.decision === 'approved').length,
      declinedThisWeek: recentDecisions.filter(d => d.decision === 'declined').length
    };
  };

  // Add brief to package
  const addToPackage = (packageId) => {
    const targetPackage = packages.find(p => p.id === packageId);
    const briefToAdd = {
      id: Date.now(),
      ...briefData,
      trend: selectedTrend,
      research: savedResearch,
      categories: selectedTrend.categories || [selectedTrend.macro],
      addedAt: new Date().toLocaleString()
    };
    setPackages(prev => prev.map(pkg =>
      pkg.id === packageId
        ? { ...pkg, briefs: [...pkg.briefs, briefToAdd] }
        : pkg
    ));
    // Log to decision history
    const historyEntry = {
      id: Date.now(),
      trend: selectedTrend.signal,
      macro: selectedTrend.macro,
      micro: selectedTrend.micro,
      decision: 'approved',
      score: opportunityScore?.overall || 0,
      decidedAt: new Date().toLocaleString(),
      decidedBy: 'Trend Strategist',
      routing: 'package',
      packageName: targetPackage?.name || 'New Package'
    };
    setDecisionHistory(prev => [historyEntry, ...prev]);

    // Track the content brief generation (packaged)
    const newBriefRecord = {
      id: Date.now(),
      trendId: selectedTrend?.id,
      trend: selectedTrend?.signal,
      macro: selectedTrend?.macro,
      briefTypes: ['content'],
      contentBrief: { status: 'packaged', destination: targetPackage?.name || 'New Package', sentAt: null },
      audienceBrief: null,
      createdAt: new Date().toLocaleDateString()
    };
    setGeneratedBriefs(prev => {
      const existing = prev.find(b => b.trendId === selectedTrend?.id);
      if (existing) {
        return prev.map(b => b.trendId === selectedTrend?.id
          ? { ...b, briefTypes: [...new Set([...b.briefTypes, 'content'])], contentBrief: newBriefRecord.contentBrief }
          : b
        );
      }
      return [newBriefRecord, ...prev];
    });

    setShowPackageModal(false);
    // Reset and go back to dashboard
    resetWorkflow();
  };

  // Create new package with this brief
  const createNewPackage = () => {
    const packageName = `${selectedTrend.micro} Package`;
    const newPackage = {
      id: Date.now(),
      name: packageName,
      macroTrend: selectedTrend.micro,
      status: 'in_progress',
      briefs: [{
        id: Date.now(),
        ...briefData,
        trend: selectedTrend,
        research: savedResearch,
        categories: selectedTrend.categories || [selectedTrend.macro],
        addedAt: new Date().toLocaleString()
      }],
      categories: selectedTrend.categories || [selectedTrend.macro],
      createdAt: new Date().toISOString().split('T')[0],
      targetDelivery: ''
    };
    setPackages(prev => [...prev, newPackage]);
    // Log to decision history
    const historyEntry = {
      id: Date.now(),
      trend: selectedTrend.signal,
      macro: selectedTrend.macro,
      micro: selectedTrend.micro,
      decision: 'approved',
      score: opportunityScore?.overall || 0,
      decidedAt: new Date().toLocaleString(),
      decidedBy: 'Trend Strategist',
      routing: 'package',
      packageName: packageName
    };
    setDecisionHistory(prev => [historyEntry, ...prev]);

    // Track the content brief generation (packaged)
    const newBriefRecord = {
      id: Date.now(),
      trendId: selectedTrend?.id,
      trend: selectedTrend?.signal,
      macro: selectedTrend?.macro,
      briefTypes: ['content'],
      contentBrief: { status: 'packaged', destination: packageName, sentAt: null },
      audienceBrief: null,
      createdAt: new Date().toLocaleDateString()
    };
    setGeneratedBriefs(prev => {
      const existing = prev.find(b => b.trendId === selectedTrend?.id);
      if (existing) {
        return prev.map(b => b.trendId === selectedTrend?.id
          ? { ...b, briefTypes: [...new Set([...b.briefTypes, 'content'])], contentBrief: newBriefRecord.contentBrief }
          : b
        );
      }
      return [newBriefRecord, ...prev];
    });

    setShowPackageModal(false);
    // Reset and go back to dashboard
    resetWorkflow();
  };

  // Send directly to CSA
  const sendToCSA = () => {
    // Log to decision history
    const historyEntry = {
      id: Date.now(),
      trend: selectedTrend.signal,
      macro: selectedTrend.macro,
      micro: selectedTrend.micro,
      decision: 'approved',
      score: opportunityScore?.overall || 0,
      decidedAt: new Date().toLocaleString(),
      decidedBy: 'Trend Strategist',
      routing: 'csa',
      packageName: null
    };
    setDecisionHistory(prev => [historyEntry, ...prev]);

    // Track the content brief generation
    const newBriefRecord = {
      id: Date.now(),
      trendId: selectedTrend?.id,
      trend: selectedTrend?.signal,
      macro: selectedTrend?.macro,
      briefTypes: ['content'],
      contentBrief: { status: 'sent', destination: 'csa', sentAt: new Date().toLocaleString() },
      audienceBrief: null,
      createdAt: new Date().toLocaleDateString()
    };
    setGeneratedBriefs(prev => {
      const existing = prev.find(b => b.trendId === selectedTrend?.id);
      if (existing) {
        return prev.map(b => b.trendId === selectedTrend?.id
          ? { ...b, briefTypes: [...new Set([...b.briefTypes, 'content'])], contentBrief: newBriefRecord.contentBrief }
          : b
        );
      }
      return [newBriefRecord, ...prev];
    });

    setNotification('Brief sent to Content Scaling Agent via SEO API for processing.');
    resetWorkflow();
  };

  // Reset workflow state back to dashboard
  const resetWorkflow = () => {
    setSelectedTrend(null);
    setWorkflowStep(0);
    setActiveView('dashboard');
    setOpportunityScore(null);
    setBriefData(null);
    setSavedResearch([]);
  };

  return {
    // State
    activeView,
    selectedTrend,
    workflowStep,
    opportunityScore,
    briefData,
    showMaia,
    selectedAgents,
    maiaQuery,
    maiaResponse,
    maiaHistory,
    savedResearch,
    researchBriefs,
    quickMaiaQuery,
    showQuickMaia,
    packages,
    showPackageModal,
    decisionHistory,
    selectedPackage,
    audienceBriefData,
    showAudienceBrief,
    activeDomain,
    generatedBriefs,
    sectionsCollapsed,
    notification,

    // Setters needed by child components
    setActiveView,
    setSelectedTrend,
    setWorkflowStep,
    setOpportunityScore,
    setBriefData,
    setShowMaia,
    setSelectedAgents,
    setMaiaQuery,
    setMaiaResponse,
    setMaiaHistory,
    setSavedResearch,
    setResearchBriefs,
    setQuickMaiaQuery,
    setShowQuickMaia,
    setPackages,
    setShowPackageModal,
    setDecisionHistory,
    setSelectedPackage,
    setAudienceBriefData,
    setShowAudienceBrief,
    setActiveDomain,
    setGeneratedBriefs,
    setSectionsCollapsed,
    setNotification,

    // Handler functions
    toggleSection,
    generateMaiaPrompt,
    openMaiaWithContext,
    toggleAgent,
    runMaiaQuery,
    saveResearchToOpportunity,
    discardResearch,
    selectTrend,
    calculateScore,
    approveTrend,
    generateAudienceBrief,
    getStats,
    addToPackage,
    createNewPackage,
    sendToCSA,
    resetWorkflow,

    // Notification
    clearNotification: () => setNotification(null),
  };
};

export default useInsightsState;
